import API, { AssetDownload, DownloadHistoryFilters } from "@/api/api";
import { useApiInstance } from "@/providers/api-provider";
import { create } from "zustand";

import createSelectors from "./create-selectors";

type Asset = {
  asset_name: string;
  asset_description: string;
  asset_path: string;
  version: string;
  download_name: string;
  size: string;
  release_month: string;
  architecture: string;
  operating_system: string;
  asset_type: string;
  asset_category: string;
  asset_key: string;
  calendar_version: string;
  semantic_version: string;
  release_year: string;
  release_day: string;
  asset_group: string;
  asset_subgroup?: string;
  display_version: string;
  display_release_month: string;
  installer_type?: string;
  // Checksum fields
  sha256: string;
  sha1: string;
  // Detected OS field
  detected_operating_system: string;
  // Pull command for Helm/Docker
  pull_command: string;
};

type AssetStoreState = {
  assets: Asset[];
  downloadHistory: AssetDownload[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  fetchAssets: () => Promise<Asset[]>;
  checkForUpdates: () => Promise<{ hasUpdates: boolean; lastUpdated: Date | null }>;
  fetchAssetDownloadUrl: (assetPath: string) => Promise<{ url: string }>;
  trackDownload: (
    assetPath: string,
    assetName: string,
    assetVersion?: string,
    fileSize?: string,
  ) => Promise<{ url: string; download_id: number }>;
  completeDownload: (downloadId: number, durationMs: number) => Promise<void>;
  fetchDownloadHistory: (filters: DownloadHistoryFilters) => Promise<void>;
};

const api = new API();

const useAssetStoreBase = create<AssetStoreState>()((set, get) => ({
  assets: [],
  downloadHistory: [],
  isLoading: false,
  error: null,
  lastUpdated: null,
  fetchAssets: async (): Promise<Asset[]> => {
    set({ isLoading: true, error: null });
    try {
      const response = await useApiInstance.get("/api/v1/assets/list", {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });
      console.info("API Response:", response);
      console.info("Response data:", response.data);

      // Handle different response formats
      const assets = Array.isArray(response.data) ? response.data : response.data?.assets || [];
      const lastUpdated = response.data?.last_updated ? new Date(response.data.last_updated) : null;

      set({ assets, lastUpdated, isLoading: false, error: null });
      console.info("Fetched assets count:", assets.length);
      return assets;
    } catch (error) {
      console.error("Error fetching assets:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to fetch assets";
      set({ isLoading: false, error: errorMessage, assets: [] });
      return [];
    }
  },
  checkForUpdates: async () => {
    try {
      const response = await useApiInstance.get("/api/v1/assets/list", {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      const serverLastUpdated = response.data?.last_updated ? new Date(response.data.last_updated) : null;
      const currentLastUpdated = get().lastUpdated;

      const hasUpdates = serverLastUpdated && currentLastUpdated ? serverLastUpdated > currentLastUpdated : false;

      return { hasUpdates, lastUpdated: serverLastUpdated };
    } catch (error) {
      console.error("Error checking for updates:", error);
      return { hasUpdates: false, lastUpdated: null };
    }
  },
  fetchAssetDownloadUrl: async (assetPath: string): Promise<{ url: string }> => {
    try {
      const response = await useApiInstance.post(`/api/v1/assets/download`, {
        path: assetPath,
      });
      console.info("Fetched asset download URL:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching asset download URL:", error);
      throw error;
    }
  },
  trackDownload: async (assetPath: string, assetName: string, assetVersion?: string, fileSize?: string) => {
    try {
      const params = new URLSearchParams({
        path: assetPath,
        asset_name: assetName,
      });
      // Use the provided version, or fallback to empty string to avoid undefined
      if (assetVersion) {
        params.append("asset_version", assetVersion);
      }
      if (fileSize) {
        params.append("file_size", fileSize);
      }
      const response = await useApiInstance.post(`/api/v1/assets/download-tracked?${params.toString()}`);
      console.info("Tracked download:", response.data);
      return {
        url: response.data.url,
        download_id: response.data.download_id,
      };
    } catch (error) {
      console.error("Error tracking download:", error);
      throw error;
    }
  },
  completeDownload: async (downloadId: number, durationMs: number) => {
    try {
      await api.completeDownload(downloadId, durationMs);
      console.info("Completed download:", downloadId);
    } catch (error) {
      console.error("Error completing download:", error);
      // Don't throw - this is best effort
    }
  },
  fetchDownloadHistory: async (filters: DownloadHistoryFilters) => {
    try {
      const response = await api.getDownloadHistory(filters);
      set({ downloadHistory: response.downloads });
      console.info("Fetched download history:", response.downloads.length);
    } catch (error) {
      console.error("Error fetching download history:", error);
      throw error;
    }
  },
}));

export const useAssetStore = createSelectors(useAssetStoreBase);
export type { Asset };
